import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import orderService from '../services/orderService';
import { useAppSelector } from '../store/hooks';
import OrderForm from '../components/OrderForm'; // Import the new OrderForm component
import { type Order } from '../types'; // Import Order type
import { useTranslation } from 'react-i18next';
import { GhostSelect } from '../components/ui/GhostSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faEye } from '@fortawesome/free-solid-svg-icons';

function OrdersPage() {
  const { t } = useTranslation();
  const user = useAppSelector((s) => s.auth.user);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'create'>('list');
  const [paymentLink, setPaymentLink] = useState<string | null>(null); // State to store payment link
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 20; // Set the limit to 20 orders per page

  // Filter states
  const [filterTotalMin, setFilterTotalMin] = useState<string>('');
  const [filterTotalMax, setFilterTotalMax] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');
  const [filterStartTime, setFilterStartTime] = useState<string>('');
  const [filterEndTime, setFilterEndTime] = useState<string>('');
  const [filterClientName, setFilterClientName] = useState<string>('');
  const [filterProductType, setFilterProductType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const fetchOrders = async () => {
    if (!user || !user.comercioKey) {
      setLoading(false);
      setError(t('ordersPage.errors.noCommerceKey'));
      return;
    }
    try {
      // Fetch all orders first, then apply pagination on the client-side
      // For a real application, you would pass page and limit to the API
      const data = await orderService.getOrders(user.comercioKey);
      setOrders(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('ordersPage.errors.load'));
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user, currentPage]); // Add currentPage to dependencies to re-fetch/re-filter on page change

  const filteredOrders = React.useMemo(() => {
    const startIndex = (currentPage - 1) * ordersPerPage;
    const endIndex = startIndex + ordersPerPage;

    return orders.filter(order => {
      const orderDate = new Date(order.created_at);
      const orderTotal = Number(order.total);

      // Filter by total
      if (filterTotalMin !== '' && orderTotal < parseFloat(filterTotalMin)) return false;
      if (filterTotalMax !== '' && orderTotal > parseFloat(filterTotalMax)) return false;

      // Filter by date range
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (orderDate < start) return false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (orderDate > end) return false;
      }

      // Filter by time range
      if (filterStartTime && filterEndTime) {
        const orderTime = orderDate.getHours() * 60 + orderDate.getMinutes();
        const [startHour, startMinute] = filterStartTime.split(':').map(Number);
        const [endHour, endMinute] = filterEndTime.split(':').map(Number);
        const filterStartMinutes = startHour * 60 + startMinute;
        const filterEndMinutes = endHour * 60 + endMinute;

        if (orderTime < filterStartMinutes || orderTime > filterEndMinutes) return false;
      } else if (filterStartTime) {
        const orderTime = orderDate.getHours() * 60 + orderDate.getMinutes();
        const [startHour, startMinute] = filterStartTime.split(':').map(Number);
        const filterStartMinutes = startHour * 60 + startMinute;
        if (orderTime < filterStartMinutes) return false;
      } else if (filterEndTime) {
        const orderTime = orderDate.getHours() * 60 + orderDate.getMinutes();
        const [endHour, endMinute] = filterEndTime.split(':').map(Number);
        const filterEndMinutes = endHour * 60 + endMinute;
        if (orderTime > filterEndMinutes) return false;
      }

      // Filter by client name
      if (filterClientName !== '' && !order.nombre_cliente.toLowerCase().includes(filterClientName.toLowerCase())) return false;

      // Filter by product type (assuming 'items' array has a 'nombre' property for product name)
      if (filterProductType !== '' && !order.items.some(item => (item.nombre || '').toLowerCase().includes(filterProductType.toLowerCase()))) return false;

      // Filter by status
      if (filterStatus !== '' && order.estado !== filterStatus) return false;

      return true;
    }).slice(startIndex, endIndex); // Apply pagination after filtering
  }, [orders, filterTotalMin, filterTotalMax, filterStartDate, filterEndDate, filterStartTime, filterEndTime, filterClientName, filterProductType, filterStatus, currentPage, ordersPerPage]);

  const totalPages = Math.ceil(orders.length / ordersPerPage);

  const totalOrders = orders.length;

  const totalOrdersToday = orders.filter(order => {
    const today = new Date();
    const orderDate = new Date(order.created_at);
    return orderDate.getDate() === today.getDate() &&
      orderDate.getMonth() === today.getMonth() &&
      orderDate.getFullYear() === today.getFullYear();
  }).length;

  const totalOrdersWeek = orders.filter(order => {
    const today = new Date();
    const orderDate = new Date(order.created_at);
    const firstDayOfWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
    firstDayOfWeek.setHours(0, 0, 0, 0);
    const lastDayOfWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() + 6);
    lastDayOfWeek.setHours(23, 59, 59, 999);
    return orderDate >= firstDayOfWeek && orderDate <= lastDayOfWeek;
  }).length;

  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'entregado': return 'bg-green-500/20 text-green-300 border border-green-500/30 text-green-300';
      case 'cancelado': return 'bg-red-500/20 text-red-300 border border-red-500/30';
      case 'pagado': return 'bg-blue-500/20 text-blue-300 border border-blue-500/30 text-blue-300';
      case 'en_preparacion': return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
      case 'listo_para_recoger': return 'bg-purple-500/20 text-purple-300 border border-purple-500/30 text-purple-300';
      case 'en_camino': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-orange-500/20 text-orange-300 border border-orange-500/30';
    }
  };

  const handleOrderCreated = (orderId: number, _total: number, link?: string) => {
    setActiveTab('list');
    fetchOrders(); // Refresh the list of orders
    if (link) {
      setPaymentLink(link);
      // Optionally, copy to clipboard or show a toast notification
      navigator.clipboard.writeText(link).then(() => {
        alert(t('ordersPage.payment.createdCopied', { id: orderId, link }));
      }).catch(err => {
        alert(t('ordersPage.payment.createdCopyFailed', { id: orderId, link, error: String(err) }));
      });
    }
    navigate(`/order-detail/${orderId}`); // Redirect to order detail page
  };

  if (loading) return <p className="text-center text-white/60 mt-8">{t('ordersPage.loading')}</p>;
  if (error) return <p className="text-center text-red-600 mt-8">{t('ordersPage.errorPrefix')}{error}</p>;
  if (!user || !user.comercioKey) return <p className="text-center text-white/60 mt-8">{t('ordersPage.needLogin')}</p>;

  return (
    <div className="space-y-6 max-w-7xl mx-auto dark-bg overflow-x-hidden">

      {activeTab === 'list' && (
        <div className="space-y-6 fade-in">
          <div className="bg-black/20 backdrop-blur-xl rounded-3xl shadow-xl p-6 border border-white/20 mb-6">
            <div className="flex justify-between items-center mb-4 p-2">
              <h2 className="text-2xl font-semibold text-white/80">{t('ordersPage.filters.title')}</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setActiveTab('create')}
                  className="btn-taratic btn-blue btn-sm rounded-full text-sm font-bold"
                >
                  <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
                  {t('ordersPage.createNew')}
                </button>
                <button onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)} className="btn-taratic btn-ghost-dark btn-sm rounded-full text-sm font-bold">{isFiltersCollapsed ? t('ordersPage.filters.show') : t('ordersPage.filters.hide')}</button>
              </div>
            </div>
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-2 rounded-b-lg shadow-md ${isFiltersCollapsed ? 'hidden' : 'grid'}`}>
              <div>
                <label htmlFor="filterTotalMin" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.totalMin')}</label>
                <input
                  type="number"
                  id="filterTotalMin"
                  value={filterTotalMin}
                  onChange={(e) => setFilterTotalMin(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                  step="0.01"
                />
              </div>
              <div>
                <label htmlFor="filterTotalMax" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.totalMax')}</label>
                <input
                  type="number"
                  id="filterTotalMax"
                  value={filterTotalMax}
                  onChange={(e) => setFilterTotalMax(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                  step="0.01"
                />
              </div>
              <div>
                <label htmlFor="filterStartDate" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.startDate')}</label>
                <input
                  type="date"
                  id="filterStartDate"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                />
              </div>
              <div>
                <label htmlFor="filterEndDate" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.endDate')}</label>
                <input
                  type="date"
                  id="filterEndDate"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                />
              </div>
              <div>
                <label htmlFor="filterStartTime" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.startTime')}</label>
                <input
                  type="time"
                  id="filterStartTime"
                  value={filterStartTime}
                  onChange={(e) => setFilterStartTime(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                />
              </div>
              <div>
                <label htmlFor="filterEndTime" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.endTime')}</label>
                <input
                  type="time"
                  id="filterEndTime"
                  value={filterEndTime}
                  onChange={(e) => setFilterEndTime(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                />
              </div>
              <div>
                <label htmlFor="filterClientName" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.clientNameLabel')}</label>
                <input
                  type="text"
                  id="filterClientName"
                  value={filterClientName}
                  onChange={(e) => setFilterClientName(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                  placeholder={t('ordersPage.filters.clientNamePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="filterProductType" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.productTypeLabel')}</label>
                <input
                  type="text"
                  id="filterProductType"
                  value={filterProductType}
                  onChange={(e) => setFilterProductType(e.target.value)}
                  className="mt-1 block w-full bg-black/20 backdrop-blur-xl border border-white/20 text-white placeholder-white/50 focus:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 rounded-2xl py-2 px-4 shadow-sm transition-all duration-300"
                  placeholder={t('ordersPage.filters.productTypePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="filterStatus" className="block text-sm font-medium text-white/80 mb-2">{t('ordersPage.filters.statusLabel')}</label>
                <GhostSelect
                  value={filterStatus}
                  onChange={(val: string) => setFilterStatus(val)}
                  className="w-full mt-1"
                  placeholder={t('ordersPage.filters.statusAll') || "Todos los estados"}
                  options={[
                    { value: "", label: t('ordersPage.filters.statusAll') || "Todos los estados" },
                    { value: "pendiente", label: t('ordersPage.filters.statusOptions.pending') || "Pendiente" },
                    { value: "pagado", label: t('ordersPage.filters.statusOptions.paid') || "Pagado" },
                    { value: "en_preparacion", label: t('ordersPage.filters.statusOptions.preparing') || "En preparación" },
                    { value: "listo_para_recoger", label: t('ordersPage.filters.statusOptions.ready_for_pickup') || "Listo para recoger" },
                    { value: "en_camino", label: t('ordersPage.filters.statusOptions.on_the_way') || "En camino" },
                    { value: "entregado", label: t('ordersPage.filters.statusOptions.delivered') || "Entregado" },
                    { value: "cancelado", label: t('ordersPage.filters.statusOptions.canceled') || "Cancelado" }
                  ]}
                />
              </div>
            </div>
          </div>

          {paymentLink && (
            <div className="bg-blue-500/20 text-blue-300 border border-blue-500/30 border-l-4 border-blue-500 text-blue-700 p-4 mb-4" role="alert">
              <p className="font-bold">{t('ordersPage.payment.generatedLabel')}</p>
              <p className="break-all">{paymentLink}</p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(paymentLink);
                  alert(t('ordersPage.payment.copied'));
                }}
                className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
              >
                {t('ordersPage.payment.copy')}
              </button>
            </div>
          )}

          <div className="bg-black/30 backdrop-blur-xl shadow-xl rounded-3xl overflow-hidden border border-white/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-white">{t('ordersPage.listTitle')}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
              <div className="bg-blue-500/20 text-blue-300 border border-blue-500/30 p-4 rounded-lg shadow-md">
                <p className="text-lg font-semibold text-blue-300">{t('ordersPage.cards.totalOrders')}</p>
                <p className="text-3xl font-bold text-blue-200">{totalOrders}</p>
              </div>
              <div className="bg-green-500/20 text-green-300 border border-green-500/30 p-4 rounded-lg shadow-md">
                <p className="text-lg font-semibold text-green-300">{t('ordersPage.cards.today')}</p>
                <p className="text-3xl font-bold text-green-200">{totalOrdersToday}</p>
              </div>
              <div className="bg-purple-500/20 text-purple-300 border border-purple-500/30 p-4 rounded-lg shadow-md">
                <p className="text-lg font-semibold text-purple-300">{t('ordersPage.cards.week')}</p>
                <p className="text-3xl font-bold text-purple-200">{totalOrdersWeek}</p>
              </div>
            </div>

            {filteredOrders.length === 0 ? (
              <p className="text-white/60 text-center">{t('ordersPage.emptyFilters')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-black/75 border-b border-white/20">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">{t('ordersPage.table.headers.order')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">{t('ordersPage.table.headers.client')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">{t('ordersPage.table.headers.items')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">{t('ordersPage.table.headers.total')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">{t('ordersPage.table.headers.date')}</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">{t('ordersPage.table.headers.status')}</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">{t('ordersPage.table.headers.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {filteredOrders.map(order => (
                      <tr key={order.id} className="hover:bg-black cursor-pointer transition-colors duration-200">
                        <td className="px-4 py-3 align-middle text-sm font-medium text-white font-semibold">{order.id}</td>
                        <td className="px-4 py-3 align-middle text-sm text-white/80">{order.nombre_cliente}</td>
                        <td className="px-4 py-3 align-middle text-sm text-white/80">
                          <div className="flex items-center space-x-2">
                            <div className="flex -space-x-2 overflow-hidden">
                              {(order.items || []).slice(0, 3).map((item: any, idx: number) => (
                                <img key={idx} src={item.imagen_url || '/no_image_available.svg'} alt={item.nombre} className="w-8 h-8 object-cover rounded-md border" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/no_image_available.svg' }} />
                              ))}
                            </div>
                            <span className="text-xs text-white/70">{(order.items || []).length} items</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-green-400 font-bold">{Number(order.total).toFixed(2)}€</td>
                        <td className="px-4 py-3 align-middle text-sm text-white/80">{new Date(order.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3 align-middle text-center">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClasses(order.estado)}`}>{order.estado.replace(/_/g, ' ').toUpperCase()}</span>
                        </td>
                        <td className="px-4 py-3 align-middle text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Link to={`/order-detail/${order.id}`} className="btn-taratic btn-orange btn-sm rounded-full" title={t('ordersPage.viewDetail')}>
                              <FontAwesomeIcon icon={faEye} />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8 space-x-4">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn-taratic btn-ghost-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('ordersPage.pagination.prev')}
              </button>
              <span className="text-lg font-semibold text-white/80">
                {t('ordersPage.pagination.pageOf', { current: currentPage, total: totalPages })}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn-taratic btn-ghost-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('ordersPage.pagination.next')}
              </button>
            </div>
          )}
        </div>
      )}

      {(activeTab === 'create') && (
        <OrderForm
          onOrderCreated={handleOrderCreated}
          onClose={() => setActiveTab('list')}
        />
      )}
    </div>
  );
}

export default OrdersPage;
